"use client"

import { HttpTypes } from "@medusajs/types"
import BlurImage from "@modules/common/components/blur-image"
import { useState, useRef, useEffect, useCallback } from "react"
import { ChevronLeft, ChevronRight, X } from "lucide-react"

type Props = {
  images: HttpTypes.StoreProductImage[]
}

const AUTO_INTERVAL = 4000
const SWIPE_THRESHOLD = 50

/**
 * Product image gallery — uses single-image display (not a strip) to
 * completely avoid React hydration removeChild errors.
 *
 * Only the currently selected image is rendered in the main viewport.
 * This guarantees SSR output matches client render regardless of state.
 */
export default function PetSelectImageGallery({ images }: Props) {
  const [selected, setSelected] = useState(0)
  const [mounted, setMounted] = useState(false)
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const thumbsRef = useRef<HTMLDivElement>(null)
  const touchStartX = useRef(0)
  const touchEndX = useRef(0)
  const autoTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const userInteractedRef = useRef(false)

  const count = images?.length || 0

  const goTo = useCallback((idx: number) => {
    setSelected(idx)
    userInteractedRef.current = true
  }, [])

  const prev = useCallback(() => {
    setSelected((s) => (s === 0 ? count - 1 : s - 1))
    userInteractedRef.current = true
  }, [count])

  const next = useCallback(() => {
    setSelected((s) => (s === count - 1 ? 0 : s + 1))
    userInteractedRef.current = true
  }, [count])

  const openLightbox = useCallback(() => {
    setIsLightboxOpen(true)
    userInteractedRef.current = true
  }, [])

  const closeLightbox = useCallback(() => {
    setIsLightboxOpen(false)
  }, [])

  // Mark as mounted — autoplay only starts after hydration is complete
  useEffect(() => {
    setMounted(true)
  }, [])

  // Autoplay — only client-side, after mount
  useEffect(() => {
    if (!mounted || count <= 1) return

    autoTimerRef.current = setInterval(() => {
      if (!userInteractedRef.current) {
        setSelected((s) => (s === count - 1 ? 0 : s + 1))
      }
    }, AUTO_INTERVAL)

    // Resume auto-play after 8s of no interaction
    const resumeTimer = setInterval(() => {
      if (userInteractedRef.current) {
        userInteractedRef.current = false
      }
    }, AUTO_INTERVAL * 2)

    return () => {
      if (autoTimerRef.current) clearInterval(autoTimerRef.current)
      clearInterval(resumeTimer)
    }
  }, [count, mounted])

  // Lightbox keyboard nav
  useEffect(() => {
    if (!isLightboxOpen) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox()
      if (count > 1 && e.key === "ArrowLeft") prev()
      if (count > 1 && e.key === "ArrowRight") next()
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [closeLightbox, count, isLightboxOpen, next, prev])

  // Touch handlers for swipe
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
    touchEndX.current = e.touches[0].clientX
  }

  const onTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX
  }

  const onTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current
    if (Math.abs(diff) > SWIPE_THRESHOLD) {
      if (diff > 0) next()
      else prev()
    }
  }

  if (!images || count === 0) return null

  const currentImage = images[selected]

  return (
    <div className="flex flex-col lg:flex-row gap-4">
      {/* Main Image — only renders the CURRENT image (no strip) */}
      <div
        className="relative flex-1 aspect-square bg-gray-50 rounded-2xl overflow-hidden group touch-pan-y lg:order-2"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={openLightbox}
      >
        <BlurImage
          key={currentImage?.id || selected}
          src={currentImage?.url || ""}
          alt={`Product image ${selected + 1}`}
          fill
          className="object-cover transition-opacity duration-300"
          priority={selected === 0}
        />

        {/* Navigation Arrows — desktop only */}
        {count > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation()
                prev()
              }}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-all z-10"
              aria-label="Previous image"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                next()
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-all z-10"
              aria-label="Next image"
            >
              <ChevronRight size={20} />
            </button>
          </>
        )}

        {/* Dot indicators */}
        {count > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
            {images.map((_, idx) => (
              <button
                key={idx}
                onClick={(e) => {
                  e.stopPropagation()
                  goTo(idx)
                }}
                className={`h-2 rounded-full transition-all ${
                  selected === idx ? "bg-[#0B7A75] w-6" : "bg-gray-300 w-2"
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Mobile thumbnail strip */}
      {count > 1 && (
        <div className="flex lg:hidden gap-2 overflow-x-auto pb-2 scrollbar-thin">
          {images.map((img, idx) => (
            <button
              key={img.id}
              onClick={() => goTo(idx)}
              className={`relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                selected === idx
                  ? "border-[#0B7A75] shadow-md"
                  : "border-transparent hover:border-gray-300 opacity-60 hover:opacity-100"
              }`}
            >
              <BlurImage
                src={img.url || ""}
                alt={`View ${idx + 1}`}
                fill
                className="object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {/* Thumbnail Strip — desktop */}
      {count > 1 && (
        <div
          ref={thumbsRef}
          className="hidden lg:flex lg:order-1 lg:flex-col gap-2 overflow-y-auto lg:max-h-[600px] lg:pr-2 scrollbar-thin"
        >
          {images.map((img, idx) => (
            <button
              key={img.id}
              onClick={() => goTo(idx)}
              className={`relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                selected === idx
                  ? "border-[#0B7A75] shadow-md"
                  : "border-transparent hover:border-gray-300 opacity-60 hover:opacity-100"
              }`}
            >
              <BlurImage
                src={img.url || ""}
                alt={`View ${idx + 1}`}
                fill
                className="object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {isLightboxOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 p-2 rounded-full"
            aria-label="Close fullscreen image"
          >
            <X size={24} />
          </button>

          {count > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  prev()
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white bg-black/50 hover:bg-black/70 p-3 rounded-full"
                aria-label="Previous image"
              >
                <ChevronLeft size={28} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  next()
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white bg-black/50 hover:bg-black/70 p-3 rounded-full"
                aria-label="Next image"
              >
                <ChevronRight size={28} />
              </button>
            </>
          )}

          <div
            className="relative w-full h-full max-w-6xl max-h-[92vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <BlurImage
              src={images[selected]?.url || ""}
              alt={`Fullscreen image ${selected + 1}`}
              fill
              className="object-contain"
              priority
            />
          </div>
        </div>
      )}
    </div>
  )
}
